import React, { useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOCATION_TYPES = [
  { value: "winkel", label: "Winkel" },
  { value: "magazijn", label: "Magazijn" },
  { value: "pakketpunt", label: "Pakketpunt" },
  { value: "kantoor", label: "Kantoor" },
  { value: "logistieke_partner", label: "Logistieke partner" },
  { value: "overig", label: "Overig" },
];

const DESIRED_ROLES = [
  { value: "agent", label: "Agent" },
  { value: "pakketpunt", label: "Pakketpunt" },
  { value: "pickup_dropoff", label: "Pickup/drop-off locatie" },
  { value: "franchise", label: "Franchise / interesse" },
  { value: "anders", label: "Anders" },
];

export default function AgentApplicationForm() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "", kvk_number: "",
    email: "", phone: "", whatsapp: "", website: "",
    country: "Nederland", city: "", address: "", postal_code: "",
    location_type: "", available_storage: "", opening_hours: "",
    has_staff: null, has_logistics_experience: null, short_description: "",
    desired_role: "", desired_destinations: "", expected_shipments: "",
    note: "", consent_privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submission = useRef(null);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent_privacy) {
      setError("Ga akkoord met de gegevensverwerking om te kunnen indienen.");
      return;
    }
    setSubmitting(true);
    try {
      const fingerprint = JSON.stringify(form);
      if (submission.current?.fingerprint !== fingerprint) submission.current = {fingerprint, key:crypto.randomUUID()};
      await axios.post(`${BACKEND_URL}/api/agent-application`, form, {
        headers: { "Content-Type": "application/json", "Idempotency-Key": submission.current.key },
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Verzenden mislukt. Controleer je gegevens.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-emerald-50 border-l-4 border-emerald-500 px-6 py-7" data-testid="agent-application-success">
        <div className="flex items-start gap-4">
          <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </span>
          <div>
            <h3 className="font-chivo font-bold text-xl text-emerald-900">Bedankt voor uw aanmelding</h3>
            <p className="text-[15px] text-emerald-800 mt-1.5">
              Wij nemen contact met u op om de mogelijkheden te bespreken.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={onSubmit} data-testid="agent-application-form">
      {/* --- Ondernemer --- */}
      <fieldset className="space-y-3">
        <legend className="text-xs tracking-wider uppercase font-chivo font-bold text-[#1a5193]">Uw gegevens</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Voornaam *</Label><Input data-testid="agent-input-first-name" required value={form.first_name} onChange={set("first_name")} className="rounded-xl" /></div>
          <div><Label>Achternaam *</Label><Input data-testid="agent-input-last-name" required value={form.last_name} onChange={set("last_name")} className="rounded-xl" /></div>
          <div className="sm:col-span-2"><Label>Bedrijfsnaam *</Label><Input data-testid="agent-input-company" required value={form.company_name} onChange={set("company_name")} className="rounded-xl" /></div>
          <div><Label>KvK / registratienummer</Label><Input data-testid="agent-input-kvk" value={form.kvk_number} onChange={set("kvk_number")} className="rounded-xl" /></div>
          <div><Label>Website / social</Label><Input data-testid="agent-input-website" value={form.website} onChange={set("website")} placeholder="https://" className="rounded-xl" /></div>
          <div><Label>E-mailadres *</Label><Input data-testid="agent-input-email" required type="email" value={form.email} onChange={set("email")} className="rounded-xl" /></div>
          <div><Label>Telefoonnummer *</Label><Input data-testid="agent-input-phone" required value={form.phone} onChange={set("phone")} placeholder="+31..." className="rounded-xl" /></div>
          <div className="sm:col-span-2"><Label>WhatsApp nummer</Label><Input data-testid="agent-input-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+31..." className="rounded-xl" /></div>
        </div>
      </fieldset>

      {/* --- Vestiging --- */}
      <fieldset className="space-y-3">
        <legend className="text-xs tracking-wider uppercase font-chivo font-bold text-[#1a5193]">Vestiging / locatie</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Land *</Label><Input data-testid="agent-input-country" required value={form.country} onChange={set("country")} className="rounded-xl" /></div>
          <div><Label>Stad *</Label><Input data-testid="agent-input-city" required value={form.city} onChange={set("city")} className="rounded-xl" /></div>
          <div className="sm:col-span-2"><Label>Adres vestiging</Label><Input data-testid="agent-input-address" value={form.address} onChange={set("address")} className="rounded-xl" /></div>
          <div><Label>Postcode</Label><Input data-testid="agent-input-postal" value={form.postal_code} onChange={set("postal_code")} className="rounded-xl" /></div>
          <div>
            <Label>Type locatie</Label>
            <select data-testid="agent-input-location-type" value={form.location_type} onChange={set("location_type")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
              <option value="">— Kies —</option>
              {LOCATION_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div><Label>Beschikbare opslagruimte</Label><Input data-testid="agent-input-storage" value={form.available_storage} onChange={set("available_storage")} placeholder="bijv. 50 m²" className="rounded-xl" /></div>
          <div><Label>Openingstijden</Label><Input data-testid="agent-input-hours" value={form.opening_hours} onChange={set("opening_hours")} placeholder="ma-vr 9-18u" className="rounded-xl" /></div>
          <div>
            <Label>Personeel beschikbaar?</Label>
            <select data-testid="agent-input-staff" value={form.has_staff === null ? "" : String(form.has_staff)} onChange={(e) => set("has_staff")(e.target.value === "" ? null : e.target.value === "true")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
              <option value="">— Kies —</option><option value="true">Ja</option><option value="false">Nee</option>
            </select>
          </div>
          <div>
            <Label>Ervaring met pakket/cargo/logistiek?</Label>
            <select data-testid="agent-input-experience" value={form.has_logistics_experience === null ? "" : String(form.has_logistics_experience)} onChange={(e) => set("has_logistics_experience")(e.target.value === "" ? null : e.target.value === "true")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
              <option value="">— Kies —</option><option value="true">Ja</option><option value="false">Nee</option>
            </select>
          </div>
          <div className="sm:col-span-2"><Label>Korte toelichting</Label><Textarea data-testid="agent-input-description" rows={3} value={form.short_description} onChange={set("short_description")} className="rounded-xl" /></div>
        </div>
      </fieldset>

      {/* --- Interesse --- */}
      <fieldset className="space-y-3">
        <legend className="text-xs tracking-wider uppercase font-chivo font-bold text-[#1a5193]">Interesse</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Gewenste rol</Label>
            <select data-testid="agent-input-role" value={form.desired_role} onChange={set("desired_role")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
              <option value="">— Kies —</option>
              {DESIRED_ROLES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div><Label>Gewenste bestemmingen</Label><Input data-testid="agent-input-destinations" value={form.desired_destinations} onChange={set("desired_destinations")} placeholder="bijv. Curaçao, Aruba, Suriname" className="rounded-xl" /></div>
          <div><Label>Verwacht aantal zendingen</Label><Input data-testid="agent-input-shipments" value={form.expected_shipments} onChange={set("expected_shipments")} placeholder="per week/maand" className="rounded-xl" /></div>
          <div className="sm:col-span-2"><Label>Opmerking</Label><Textarea data-testid="agent-input-note" rows={3} value={form.note} onChange={set("note")} className="rounded-xl" /></div>
        </div>
      </fieldset>

      {/* --- Consent --- */}
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <Checkbox data-testid="agent-input-consent" checked={form.consent_privacy} onCheckedChange={(v) => setForm((f) => ({ ...f, consent_privacy: !!v }))} />
        <span>Ik ga akkoord dat 5999Cargo mijn gegevens gebruikt om contact op te nemen over mijn agent-aanmelding.</span>
      </label>

      {error && (
        <div data-testid="agent-application-error" className="bg-red-50 border-l-4 border-red-500 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}

      <Button data-testid="agent-application-submit" type="submit" disabled={submitting} className="w-full sm:w-auto bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-chivo font-bold uppercase text-sm px-8 py-6 shadow-brand-soft hover:shadow-brand-glow active:scale-[0.98] transition-all">
        {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verzenden...</>) : "Aanmelding versturen"}
      </Button>
    </form>
  );
}
